import React, { useState } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';

import {
  FlowProgressHeader,
  StepMatch,
} from 'views/components/FlowProgressHeader/FlowProgressHeader';
import { IPlan, PromoCodeDiscount, IExtra } from 'types';
import { RadioInput } from 'views/components/RadioInput/RadioInput';
import { FlowButtonBar } from 'views/components/FlowButtonBar/FlowButtonBar';
import { CheckboxInput } from 'views/components/CheckboxInput/CheckboxInput';
import { Icon } from 'views/components/Icon/Icon';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { Pricing } from 'views/components/Pricing/PricingController';
import { actionSaveStepPickAPlan } from 'store/modules/sign-up/actions';
import { scrollToError } from 'tools/utilities/scrollToError';
import { YesNo } from 'views/components/YesNo/YesNo';
import { PromoCodeInput } from 'views/components/Pricing/PromoCodeInput/PromoCodeInput';
import { PLAN_IDS } from 'store/modules/app/constants';
import { ReferralCodeInput } from 'views/components/Pricing/ReferralCodeInput/ReferralCodeInput';
import { FeatureList } from './FeatureList/FeatureList';

interface Props {
  intl: IntlShape;
  plans: IPlan[];
  creditCheckWarningEnabled?: boolean;
  extras: IExtra[];
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  saveStepPickAPlan: typeof actionSaveStepPickAPlan;
  chosenPlanId: string;
  chosenExtras: string[];
  isViewportSmall: boolean;

  isCharity: boolean;
  defaultPromoCode?: string;
  promoCodeDiscount?: PromoCodeDiscount;
  promoCodeIsLoading: boolean;
  promoCodeIsSuccess: boolean;
  promoCodeIsFailure: boolean;
  promoCodeErrorMessage?: string;

  referralCode?: string;

  // Actions
  setIsCharity: (isCharity: boolean) => void;
  onApplyCode: (promoCode: string) => void;
  onApplyReferralCode: (referralCode: string) => void;
}

export const PickAPlanView = ({
  intl,
  plans,
  creditCheckWarningEnabled,
  goToNextStep,
  extras,
  chosenPlanId,
  chosenExtras,
  goToPreviousStep,
  saveStepPickAPlan,
  isViewportSmall,

  isCharity,
  defaultPromoCode,
  promoCodeDiscount,
  promoCodeIsLoading,
  promoCodeIsSuccess,
  promoCodeIsFailure,
  promoCodeErrorMessage,

  referralCode,

  setIsCharity,
  onApplyCode,
  onApplyReferralCode,
}: Props) => {
  const [touchedNext, setTouchedNext] = useState(false);

  const onPressChosenExtras = (value: string) => {
    if (chosenExtras.includes(value)) {
      // Remove it
      const newChosenExtras = chosenExtras.filter((extra) => extra !== value);

      saveStepPickAPlan({
        chosenPlan: chosenPlanId,
        extras: newChosenExtras,
      });
    } else {
      // Add it
      saveStepPickAPlan({
        chosenPlan: chosenPlanId,
        extras: [...chosenExtras, value],
      });
    }
  };

  const chosenPlanIsValid = !!chosenPlanId;
  const referralCodeIsValid = promoCodeDiscount?.code !== 'REFER' || !!referralCode;
  const isValid = chosenPlanIsValid && referralCodeIsValid;

  const onNext = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    goToNextStep();
  };

  // Choose the plan if it is not disabled, otherwise force to calculate only
  const chosenPlan = plans.find((plan) => plan.id === chosenPlanId);
  const validPlanId = chosenPlan?.isDisabled ? PLAN_IDS.CALCULATE_ONLY : chosenPlanId;

  const shouldShowReferralCodeInput = (): boolean => promoCodeDiscount?.code === 'REFER';

  return (
    <div className="PickAPlan">
      <FlowProgressHeader flowMatch={StepMatch.PickAPlan} />
      <div className="Container">

        <div className="Grid">

          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <div className="PickAPlan-group">
                <FormattedMessage tagName="h2" id="SELECT_A_PLAN" />

                {/* @TODO Rework radioInput to not include the border and recommended flag */}
                <RadioInput
                  id="plan"
                  onChange={(val) => {
                    saveStepPickAPlan({
                      chosenPlan: val,
                      extras: chosenExtras,
                    });
                  }}
                  defaultValue={validPlanId}
                  options={plans.map(({ id }) => id)}
                  error={touchedNext && getErrorMessageFor(chosenPlanIsValid, 'ERROR_CHOSEN_PLAN', intl)}
                  isRecommended={(index) => !!plans[index].isRecommended}
                  className="RadioInput--hasBorder"
                  isOptionDisabled={(index) => plans[index].isDisabled || false}
                  renderWarningLabel={(index) => {
                    const {
                      smartPayroll,
                    } = plans[index];

                    if (creditCheckWarningEnabled && smartPayroll) {
                      return (
                        <div className="PickAPlan-warningMessage">
                          <Icon name="ic-info" />
                          <FormattedMessage tagName="p" id="CREDIT_CHECK_WARNING" />
                        </div>
                      );
                    }
                    return null;
                  }}
                  renderLabelFor={(index) => {
                    const {
                      title, text, isDisabled, unavailableText,
                    } = plans[index];

                    return (
                      <span>
                        <h2 className="PickAPlan-group-title">{title}</h2>

                        {isDisabled && unavailableText && (
                          <div className="PickAPlan-extra-unavailable">
                            <Icon name="ic-info" />
                            <div>
                              <p>{unavailableText}</p>
                            </div>
                          </div>
                        )}

                        <p>{text}</p>
                      </span>
                    );
                  }}
                  renderAfterLabel={(index) => {
                    const { features, isDisabled } = plans[index];
                    return (
                      <div className="PickAPlan-featureList">
                        <FeatureList
                          isDisabled={isDisabled}
                          features={features}
                          showFeaturesLabel={intl.formatMessage({ id: 'SHOW_FEATURES' })}
                          hideFeaturesLabel={intl.formatMessage({ id: 'HIDE_FEATURES' })}
                        />
                      </div>
                    );
                  }}
                />
              </div>

              {extras.length > 0 && (
                <div className="PickAPlan-group">
                  <FormattedMessage tagName="h2" id="PLAN_EXTRAS" />

                  {extras.map(({
                    id, title, text, features, isRecommended,
                  }) => (
                    <div className="PickAPlan-extra" key={id}>
                      {isRecommended && (
                        <div className="PickAPlan-extra-recommended">
                          <Icon name="ic-recommended" />
                        </div>
                      )}

                      <CheckboxInput
                        id={id}
                        value={id}
                        isChecked={chosenExtras.includes(id)}
                        onChange={(value) => onPressChosenExtras(value)}
                        renderLabel={() => (
                          <div className="PickAPlan-group-extra">
                            <h2 className="PickAPlan-group-title">{title}</h2>
                            <p>{text}</p>
                          </div>
                        )}
                      />

                      <div className="PickAPlan-extra-featureList">
                        <FeatureList
                          features={features}
                          showFeaturesLabel={intl.formatMessage({ id: 'SHOW_FEATURES' })}
                          hideFeaturesLabel={intl.formatMessage({ id: 'HIDE_FEATURES' })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="PickAPlan-inputs">
                {/* Registered charity */}
                <div className="PickAPlan-input">
                  <YesNo
                    isSideBySide
                    onChange={setIsCharity}
                    id="isCharity"
                    value={isCharity}
                    label={intl.formatMessage({ id: 'ARE_YOU_REGISTERED_CHARITY' })}
                  />
                </div>

                <div className="PickAPlan-input">
                  {/* Promo code */}
                  <PromoCodeInput
                    isLoading={promoCodeIsLoading}
                    isSuccess={promoCodeIsSuccess}
                    isError={promoCodeIsFailure}
                    errorMessage={promoCodeErrorMessage}
                    onApplyCode={onApplyCode}
                    promoCodeDiscount={promoCodeDiscount}
                    defaultValue={defaultPromoCode}
                  />
                  {/* Referral code – visible if promo code REFER is used */}
                  {shouldShowReferralCodeInput() && (
                    <ReferralCodeInput
                      activeReferralCode={referralCode}
                      onApplyReferralCode={onApplyReferralCode}
                      error={touchedNext && getErrorMessageFor(referralCodeIsValid, 'ERROR_NO_REFERRAL_CODE', intl)}
                    />
                  )}
                </div>
              </div>

              {isViewportSmall && (
                <Pricing isSmall />
              )}

              <FlowButtonBar
                secondaryLabel={intl.formatMessage({ id: 'BUTTON_BACK' })}
                onPrimary={onNext}
                onSecondary={goToPreviousStep}
              />
            </div>

          </div>

          {!isViewportSmall && (
            <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
              <Pricing />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
